export const DEEP_LINK_TYPES = Object.freeze({
  EVENT: "event",
  PROFILE: "profile",
  MESSAGES: "messages",
  SETTINGS: "settings",
  // Lien public partageable « /listevents » : menu Événements, vue
  // « Populaires en France ». Aucun identifiant, aucune connexion requise.
  EVENTS_LIST: "events-list",
});

const INTERNAL_ORIGIN = "https://karango.local";

function normalizeId(value) {
  const normalized = String(value || "").trim();
  return normalized || null;
}

function decodeSegment(segment) {
  try {
    return decodeURIComponent(segment);
  } catch (_error) {
    return segment;
  }
}

function normalizeLocation(input) {
  if (!input) {
    return { pathname: "/", search: "" };
  }

  if (typeof input === "string") {
    try {
      const url = new URL(input, INTERNAL_ORIGIN);
      return { pathname: url.pathname || "/", search: url.search || "" };
    } catch (_error) {
      return { pathname: input, search: "" };
    }
  }

  if (input instanceof URL) {
    return { pathname: input.pathname || "/", search: input.search || "" };
  }

  return {
    pathname: input.pathname || "/",
    search: input.search || "",
  };
}

function withNotificationContext(target, search) {
  if (!target) return null;
  const notificationId = normalizeId(
    new URLSearchParams(search || "").get("notificationId")
  );
  return notificationId ? { ...target, notificationId } : target;
}

function getPathSegments(pathname) {
  return String(pathname || "/")
    .split("/")
    .map((segment) => decodeSegment(segment.trim()))
    .filter(Boolean);
}

// Trajet precis a ouvrir (lien e-mail, lien de partage d'un trajet) : n'a de
// sens que pour l'onglet Covoiturage. Simple cible de navigation, jamais une
// autorisation : le serveur controle l'acces aux trajets.
function withCarpoolOfferId(target, params) {
  if (target?.subPath !== "carpool") return target;
  const offerId = normalizeId(params.get("offerId"));
  return offerId ? { ...target, offerId } : target;
}

function parseQueryFallback(search) {
  const params = new URLSearchParams(search || "");
  const eventId = normalizeId(params.get("eventId"));
  if (eventId) {
    const section = normalizeId(params.get("section") || params.get("tab"));
    return withCarpoolOfferId({
      type: DEEP_LINK_TYPES.EVENT,
      eventId,
      ...(["discussion", "carpool", "participants"].includes(section) ? { subPath: section } : {}),
      source: "query",
    }, params);
  }

  const userId = normalizeId(params.get("profile"));
  if (userId) {
    return { type: DEEP_LINK_TYPES.PROFILE, userId, source: "query" };
  }

  return null;
}

export function parseDeepLink(input) {
  const { pathname, search } = normalizeLocation(input);
  const segments = getPathSegments(pathname);
  const [section, rawId, ...restSegments] = segments;

  if (section === "events") {
    const eventId = normalizeId(rawId);
    const searchParams = new URLSearchParams(search || "");
    const requestedSection = normalizeId(
      searchParams.get("section") || searchParams.get("tab")
    );
    const pathSubPath = restSegments.join("/") || null;
    const subPath = pathSubPath ||
      (["discussion", "carpool", "participants"].includes(requestedSection)
        ? requestedSection
        : null);
    return withNotificationContext(eventId
      ? withCarpoolOfferId({
          type: DEEP_LINK_TYPES.EVENT,
          eventId,
          subPath,
          source: "path",
        }, searchParams)
      : parseQueryFallback(search), search);
  }

  if (section === "profile" || section === "profiles") {
    const userId = normalizeId(rawId);
    return withNotificationContext(userId
      ? {
          type: DEEP_LINK_TYPES.PROFILE,
          userId,
          subPath: restSegments.join("/") || null,
          source: "path",
        }
      : parseQueryFallback(search), search);
  }

  if (section === "messages") {
    return withNotificationContext({
      type: DEEP_LINK_TYPES.MESSAGES,
      threadId: normalizeId(rawId),
      source: "path",
    }, search);
  }

  if (section === "listevents" && !rawId) {
    return { type: DEEP_LINK_TYPES.EVENTS_LIST, view: "popular", source: "path" };
  }

  if (section === "settings") {
    return withNotificationContext({
      type: DEEP_LINK_TYPES.SETTINGS,
      section: normalizeId(rawId) || "notifications",
      source: "path",
    }, search);
  }

  return withNotificationContext(parseQueryFallback(search), search);
}

export function parseDeepLinkFromLocation(location) {
  if (location) {
    return parseDeepLink(location);
  }

  if (typeof window === "undefined") {
    return null;
  }

  return parseDeepLink(window.location);
}

export function deepLinkRequiresAuth(target) {
  return (
    Boolean(target?.notificationId) ||
    (target?.type === DEEP_LINK_TYPES.EVENT && Boolean(target?.subPath)) ||
    target?.type === DEEP_LINK_TYPES.PROFILE ||
    target?.type === DEEP_LINK_TYPES.MESSAGES ||
    target?.type === DEEP_LINK_TYPES.SETTINGS
  );
}

export function clearNotificationIdFromCurrentUrl() {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  if (!url.searchParams.has("notificationId")) return;
  url.searchParams.delete("notificationId");
  window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
}

export function getDeepLinkKey(target) {
  if (!target?.type) {
    return "";
  }

  if (target.type === DEEP_LINK_TYPES.EVENT) {
    const baseKey = `${target.type}:${target.eventId || ""}:${target.subPath || ""}`;
    // Deux trajets d'une meme sortie sont deux cibles distinctes.
    return target.offerId ? `${baseKey}:${target.offerId}` : baseKey;
  }

  if (target.type === DEEP_LINK_TYPES.PROFILE) {
    return `${target.type}:${target.userId || ""}:${target.subPath || ""}`;
  }

  if (target.type === DEEP_LINK_TYPES.MESSAGES) {
    return `${target.type}:${target.threadId || ""}`;
  }

  if (target.type === DEEP_LINK_TYPES.SETTINGS) {
    return `${target.type}:${target.section || ""}`;
  }

  return String(target.type);
}

export function dispatchDeepLinkNavigation(targetOrInput, handlers = {}) {
  const target = targetOrInput?.type
    ? targetOrInput
    : parseDeepLink(targetOrInput);

  if (!target?.type) {
    return null;
  }

  if (target.type === DEEP_LINK_TYPES.EVENT) {
    handlers.onOpenEvent?.(target.eventId, target);
    return target;
  }

  if (target.type === DEEP_LINK_TYPES.PROFILE) {
    handlers.onOpenProfile?.(target.userId, target);
    return target;
  }

  if (target.type === DEEP_LINK_TYPES.MESSAGES) {
    handlers.onOpenMessages?.(target.threadId || undefined, target);
    return target;
  }

  if (target.type === DEEP_LINK_TYPES.SETTINGS) {
    handlers.onOpenSettings?.(target.section || undefined, target);
    return target;
  }

  return null;
}
